import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

export default function useDashboardV2Data() {
  const { authToken } = useAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userMetrics, setUserMetrics] = useState([]);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  useEffect(() => {
    if (!authToken) return;

    const loadUser = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/user`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!response.ok) throw new Error('No se pudo cargar el usuario');
        const data = await response.json();
        setUserId(data.id);
      } catch (error) {
        console.error('Error loading dashboard user:', error);
      }
    };

    loadUser();
  }, [authToken]);

  useEffect(() => {
    if (!userId || !authToken) return;

    const loadUserInfo = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/userinfo/user/${userId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!response.ok) throw new Error('No se pudo cargar el perfil');
        setUserInfo(await response.json());
      } catch (error) {
        console.error('Error loading dashboard profile:', error);
      }
    };

    loadUserInfo();
  }, [authToken, userId]);

  useEffect(() => {
    if (!authToken) return;

    const loadSupportingData = async () => {
      try {
        const [metricsResponse, onboardingResponse] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/metrics/user-metrics`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          fetch(`${import.meta.env.VITE_API_URL}/onboarding-status`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
        ]);

        if (metricsResponse.ok) {
          const metrics = await metricsResponse.json();
          setUserMetrics(metrics.filter((metric) => metric.isTracking));
        }

        if (onboardingResponse.ok) {
          const status = await onboardingResponse.json();
          const completed = Boolean(status?.hasCompletedOnboarding);
          setHasCompletedOnboarding(completed);
          if (!completed && !localStorage.getItem('onboardingModalDismissed')) {
            setShowOnboardingModal(true);
          }
        }
      } catch (error) {
        console.error('Error loading dashboard preferences:', error);
      }
    };

    loadSupportingData();
  }, [authToken]);

  const dismissOnboardingModal = useCallback(() => {
    setShowOnboardingModal(false);
    localStorage.setItem('onboardingModalDismissed', 'true');
  }, []);

  return {
    userInfo,
    userMetrics,
    hasCompletedOnboarding,
    showOnboardingModal,
    dismissOnboardingModal,
  };
}
